import {Box, Grid, LogoSuite, Section, SectionIntro, Text, Tiles} from '@primer/react-brand/esm'
import type {ReactElement, ReactNode} from 'react'
import {t} from '@github-ui/swp-core/lib/utils/localization'
import {hasIntroSemanticHeading, renderWithoutParagraphs} from '../../../utils'
import {Logo} from '../../../components/FlexSuiteLogos/Logo'
import type {IntroLogoType} from '../../../types/templates/templateFlexSuiteDetails'
import {CopilotCliPicker} from '../CopilotCliPicker'
import type {IntroSectionProps, LogobarOptions, LogoSuiteOptions} from './types'
import {getExperimentCommands} from './utils'
import {IntroIconCards, IntroImageCards, IntroPillars, IntroSimpleIntro, IntroStats} from './variants'
import styles from './IntroSection.module.css'

export function IntroSection({
  introLogoHeading,
  introLogoType,
  introLogos,
  introLogoLink,
  settings,
  introContext,
  introEyebrow,
  introContextHeading,
  introContextLink,
  introContextBody,
  introItems,
  introIconCards,
  introPillars,
  introImageCards,
  introStats,
  logobarOptions,
  logoSuiteOptions,
  hideLogoHeading = true,
  hasMarqueeTopBorder = false,
}: IntroSectionProps) {
  const promoteFirstHeading = !hasIntroSemanticHeading({
    hasLogoHeading: introLogoType === 'Marquee' && Boolean(introLogos?.length),
    hasIntroContextHeading: Boolean(introContextHeading),
  })
  const introContextContent = renderIntroContextContent({
    settings,
    introContext,
    introEyebrow,
    introContextHeading,
    introContextLink,
    introContextBody,
    introItems,
    introIconCards,
    introPillars,
    introImageCards,
    introStats,
    promoteFirstHeading,
  })

  const hasIntroContent = Boolean(introLogos?.length) || introContextContent !== null

  if (!hasIntroContent) return null

  return (
    <Section fullWidth paddingBlockEnd="none" paddingBlockStart="none">
      {introLogos && introLogos.length > 0
        ? renderIntroLogos({
            introLogos,
            introLogoType,
            introLogoHeading,
            introLogoLink,
            logobarOptions,
            logoSuiteOptions,
            hideLogoHeading,
            hasMarqueeTopBorder,
          })
        : null}
      {introContextContent}
    </Section>
  )
}

type RenderIntroLogosArgs = {
  introLogos: string[]
  introLogoType: IntroLogoType | undefined
  introLogoHeading: IntroSectionProps['introLogoHeading']
  introLogoLink: IntroSectionProps['introLogoLink']
  logobarOptions: LogobarOptions | undefined
  logoSuiteOptions: LogoSuiteOptions | undefined
  hideLogoHeading: boolean
  hasMarqueeTopBorder: boolean
}

function renderIntroLogos({
  introLogos,
  introLogoType,
  introLogoHeading,
  introLogoLink,
  logobarOptions,
  logoSuiteOptions,
  hideLogoHeading,
  hasMarqueeTopBorder,
}: RenderIntroLogosArgs): ReactElement {
  // Tiles is the non-motion default when the author has not picked a type.
  if (introLogoType !== 'Marquee') {
    return (
      <Box marginBlockStart={48}>
        <Grid data-testid="intro-tiles">
          <Grid.Column>
            {introLogoHeading ? (
              hideLogoHeading ? (
                // Keep the heading announceable but don't reserve any visual space.
                <Text as="p" size="200" variant="muted" align="center" className="sr-only">
                  {introLogoHeading}
                </Text>
              ) : (
                <Box paddingBlockEnd={{narrow: 'spacious', regular: 64}} paddingBlockStart="spacious">
                  <Text as="p" size="200" variant="muted" align="center">
                    {introLogoHeading}
                  </Text>
                </Box>
              )
            ) : null}
            <Tiles variant="gridlines" layout="compact">
              {introLogos.map(logo => (
                <Tiles.Item key={logo} name={logo} data-testid={`intro-tile-${logo}`}>
                  <Logo name={logo} />
                </Tiles.Item>
              ))}
            </Tiles>
          </Grid.Column>
        </Grid>
      </Box>
    )
  }

  const takeoverButton =
    logobarOptions?.takeoverButton ??
    (introLogoLink?.fields.href ? {label: introLogoLink.fields.text, href: introLogoLink.fields.href} : undefined)

  return (
    <LogoSuite
      align="center"
      variant={logoSuiteOptions?.variant}
      className={hasMarqueeTopBorder ? styles.marqueeTopBorder : undefined}
    >
      <LogoSuite.Heading visuallyHidden={hideLogoHeading || !introLogoHeading}>
        {introLogoHeading || t('Logos')}
      </LogoSuite.Heading>
      <LogoSuite.Logobar
        variant={logobarOptions?.variant}
        marquee
        marqueeSpeed={logobarOptions?.marqueeSpeed}
        takeoverButton={takeoverButton}
      >
        {introLogos.map(logo => (
          <Logo key={logo} name={logo} />
        ))}
      </LogoSuite.Logobar>
    </LogoSuite>
  )
}

type IntroContextContentProps = Pick<
  IntroSectionProps,
  | 'settings'
  | 'introContext'
  | 'introEyebrow'
  | 'introContextHeading'
  | 'introContextLink'
  | 'introContextBody'
  | 'introItems'
  | 'introIconCards'
  | 'introPillars'
  | 'introImageCards'
  | 'introStats'
> & {promoteFirstHeading: boolean}

function renderIntroContextContent({
  settings,
  introContext,
  introEyebrow,
  introContextHeading,
  introContextLink,
  introContextBody,
  introItems,
  introIconCards,
  introPillars,
  introImageCards,
  introStats,
  promoteFirstHeading,
}: IntroContextContentProps): ReactNode {
  if (!introContext) return null

  const sharedIntroProps = {
    introEyebrow,
    introContextHeading,
    introContextBody,
    introContextLink,
  }

  switch (introContext) {
    case 'Simple Intro':
      return (
        <IntroSimpleIntro
          introContextHeading={introContextHeading}
          introContextBody={introContextBody}
          introContextLink={introContextLink}
          introItems={introItems}
          promoteFirstHeading={promoteFirstHeading}
        />
      )
    case 'Pillars': {
      const experimentCommands = getExperimentCommands(settings)

      return (
        <div className={styles.pillarsWithCopilot}>
          {experimentCommands.length > 0 ? <CopilotCliPicker commands={experimentCommands} /> : null}
          <SectionIntroContext {...sharedIntroProps} />
          <IntroPillars introPillars={introPillars} promoteFirstHeading={promoteFirstHeading} />
        </div>
      )
    }
    case 'Icon Cards':
      return (
        <>
          <SectionIntroContext {...sharedIntroProps} />
          <IntroIconCards introIconCards={introIconCards} promoteFirstHeading={promoteFirstHeading} />
        </>
      )
    case 'Image Cards':
      return (
        <>
          <SectionIntroContext {...sharedIntroProps} />
          <IntroImageCards introImageCards={introImageCards} promoteFirstHeading={promoteFirstHeading} />
        </>
      )
    case 'Stats':
      return (
        <>
          <SectionIntroContext {...sharedIntroProps} />
          <IntroStats introStats={introStats} />
        </>
      )
    default:
      return null
  }
}

type IntroContextSectionIntroProps = Pick<
  IntroSectionProps,
  'introEyebrow' | 'introContextHeading' | 'introContextBody' | 'introContextLink'
>

function SectionIntroContext({
  introEyebrow,
  introContextHeading,
  introContextBody,
  introContextLink,
}: IntroContextSectionIntroProps): ReactElement | null {
  if (!introContextHeading) return null

  const introContextLinkText = introContextLink?.fields.text?.trim()

  return (
    <Box paddingBlockEnd={{narrow: 'spacious', regular: 64}} paddingBlockStart={{narrow: 'spacious', regular: 64}}>
      <Grid>
        <Grid.Column>
          <SectionIntro align="center" fullWidth>
            {introEyebrow ? <SectionIntro.Label>{introEyebrow}</SectionIntro.Label> : null}
            <SectionIntro.Heading size="3">{renderWithoutParagraphs(introContextHeading)}</SectionIntro.Heading>
            {introContextBody ? (
              <SectionIntro.Description>{renderWithoutParagraphs(introContextBody)}</SectionIntro.Description>
            ) : null}
            {introContextLink?.fields.href && introContextLinkText ? (
              <SectionIntro.Link href={introContextLink.fields.href}>{introContextLinkText}</SectionIntro.Link>
            ) : null}
          </SectionIntro>
        </Grid.Column>
      </Grid>
    </Box>
  )
}
